import CMSRender from "aldoo-ra/CMS/cms-render"
import Typography from "aldoo-ra/Typography"
import parse from "html-react-parser"
import { useRef, useEffect } from "react"
import BackToTopButton from "./back-to-top-btn"
import ArticleBanner from "./banner"
import ReadTimeBadge from "./read-time-badge"
import BlogSharePanel from "./share-panel"
import PageLike from "./page-like"
import Text from "aldoo-ra/Text"
import { updateCDNUrls } from "aldoo-ra/HtmlEditor/cdn-urls"

const AD_PLACEHOLDER = "in-article-ad-placeholder"

const InArticleAd = () => {
  useEffect(() => {
    try {
      ;(window.adsbygoogle = window.adsbygoogle || []).push({})
    } catch (e) {}
  }, [])

  return (
    <ins
      className="adsbygoogle"
      style={{ display: "block", textAlign: "center" }}
      data-ad-layout="in-article"
      data-ad-format="fluid"
      data-ad-client="ca-pub-2870930144529527"
      data-ad-slot="9257126261"
    />
  )
}

function injectAdPlaceholder(html, n) {
  if (n === undefined) {
    const idx = html.lastIndexOf("</p>")
    if (idx === -1) return html
    const pos = idx + 4
    return html.slice(0, pos) + `<div id="${AD_PLACEHOLDER}"></div>` + html.slice(pos)
  }
  let count = 0
  let pos = 0
  while (count < n) {
    const idx = html.indexOf("</p>", pos)
    if (idx === -1) return html
    pos = idx + 4
    count++
  }
  return html.slice(0, pos) + `<div id="${AD_PLACEHOLDER}"></div>` + html.slice(pos)
}

const parseOptions = {
  replace(domNode) {
    if (domNode.attribs?.id === AD_PLACEHOLDER) return <InArticleAd />
  },
}

const BlogPost = ({ postID }) => {
  const contentRef = useRef(null)
  return (
    <div className="w-full bg-paper -mt-16 rounded-b-[50px] md:rounded-b-[100px] overflow-hidden">
      <CMSRender
        contentType="BlogPost"
        match={{ "content.slug": postID }}
        renderItem={(post, id) => {
          const DraftChip = () => (
            <span className="bg-admin_warning text-admin_text_inv rounded-full px-3 py-1.5 font-medium text-sm inline-block">
              Draft Article
            </span>
          )

          return (
            <div
              key={id}
              className="w-full lg:min-w-[540px] xl:min-w-[670px] lg:max-w-[750px] xl:max-w-[890px] px-4 md:px-px1_l pt-[104px] md:pt-[124px] lg:pt-[171px] pb-[108px] md:pb-[168px] mx-auto"
            >
              <BackToTopButton />

              {/* Title and Date */}
              <div className="w-full lg:max-w-[1200px] mb-[48px] mx-auto lg:mx-0">
                {post.status === "draft" && (
                  <div className="mb-4">
                    <DraftChip />
                  </div>
                )}
                
                {/* Styles for this article title differ from the variant styles so they are set here separately. */}
                <Typography
                  component="h1"
                  variant="h2"
                  className="text-[26px] sm:text-[32px] md:text-[38px] lg:text-[44px] xl:text-[52px] 2xl:text-[60px] text-left leading-[0.95] tracking-[-0.04em] font-extrabold text-text mb-[16px] ![text-wrap-style:auto]"
                >
                  {post.title}
                </Typography>

                <div className="flex items-center gap-4">
                  <Typography variant="txt1_dim">
                    {new Date(post.publish_date || post.created_date || "2024-01-01").toLocaleDateString("en", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      // UTC: the server and the browser print the same date
                      timeZone: "UTC",
                    })}
                  </Typography>
                  
                  <PageLike target={post} />
                </div>
              </div>

              {/* Article Layout */}
              <div className="w-full flex flex-col items-center justify-between mx-auto">
                {/* Main Content */}
                <div className="w-full ">
                  {/* Featured Image */}
                  <div className="relative rounded-[28px] md:rounded-[40px] overflow-hidden border-2 border-theme_black mb-[30px]">
                    <div className="relative">
                      <img
                        src={post.thumbnail}
                        alt={post.title}
                        className="w-full aspect-[694/428] xl:aspect-[890/428] object-cover"
                      />

                      <ReadTimeBadge
                        data={post}
                        top="4"
                        topMd="6"
                        right="4"
                        rightMd="8"
                      />
                    </div>
                  </div>

                  <div className="my-10 flex justify-end">
                    <BlogSharePanel url={typeof window !== 'undefined' ? window.location.href : ''} title={post.title} />
                  </div>

                  {/* Article Content */}
                  <article ref={contentRef} className="blog_post">
                    {parse(
                      post.content
                        ? injectAdPlaceholder(injectAdPlaceholder(updateCDNUrls(post.content), 2))
                        : "Empty",
                      parseOptions
                    )}
                  </article>

                  <div className="w-full flex justify-center my-10">
                    <PageLike
                      target={post}
                      variant="like_btn_big"
                      label={<Text id="like_article" fallback="Like this article" className="my-0" />}
                    />
                  </div>

                  {post.status === "draft" && (
                    <div className="mt-8 mb-2 text-center">
                      <DraftChip />
                    </div>
                  )}

                  <div className="my-[96px] flex justify-end">
                    <BlogSharePanel url={typeof window !== 'undefined' ? window.location.href : ''} title={post.title} />
                  </div>

                  <ArticleBanner />
                </div>
              </div>
            </div>
          )
        }}
      />
    </div>
  )
}
export default BlogPost
